import React from 'react';
import { Tooltip } from 'react-bootstrap';
import {
  CircleMinus,
  CirclePlus,
  InfoCircle,
  Lock,
  Minus,
  Plus,
} from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import classNames from 'classnames';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import {
  SuggestionItem,
  getSuggestionValue,
  getSuggestionKey,
  isV2Suggestion,
  isCompanySuggestion,
} from './v2-filter-checkbox.helpers';
import { accessibleOnClick } from '../../../../../shared/utils';

interface RenderAutocompleteProps {
  suggestions: SuggestionItem[];
  onSelectOption: (option: string) => void;
  filterKey: string;
  selectedSuggestionIndex: number;
  handleOnChange: (params: {
    action: string;
    groupName: string;
    childName?: string;
    exclude?: boolean;
  }) => void;
  setShowAutoComplete: (value: boolean) => void;
  isIncludeDisabled: boolean;
  includeTooltip: string;
  isV2Response: boolean;
  isLoading: boolean;
  hideIncludeExclude?: boolean;
  inputLabel?: string;
}

interface RenderCheckboxGroupProps {
  label: string;
  name: string;
  checked: boolean | 'indeterminate';
  childItems: any[];
  expanded: string[];
  handleOnChange: (params: {
    action: string;
    groupName: string;
    childName?: string;
  }) => void;
  filterKey: string;
  infoCircleToolTip?: string;
  hideIncludeExclude?: boolean;
  isIncludeDisabled: boolean;
  includeTooltip: string;
  isPhoneNumberNudgeOpen: boolean;
  onToggleExpand?: (key: string) => void;
  tooltipText?: string;
  parentLocked?: boolean;
  onLockClick?: () => void;
}

export const getLabelTooltip = (text: string) => (
  <Tooltip id="popover-basic" className="bs-tooltip-inner">
    {text}
  </Tooltip>
);

export const RenderAutocomplete: React.FC<RenderAutocompleteProps> = ({
  inputLabel,
  suggestions,
  onSelectOption,
  filterKey,
  selectedSuggestionIndex,
  handleOnChange,
  setShowAutoComplete,
  isIncludeDisabled,
  includeTooltip,
  isV2Response = false,
  isLoading = false,
  hideIncludeExclude = false,
}) => {
  if (suggestions?.length === 0 && !isLoading) {
    return null;
  }

  const skeletonCount = ['location'].includes(filterKey) ? 5 : 3;

  return (
    <ul
      role="listbox"
      className={classNames('autocomplete', {
        'autocomplete-loading': isLoading,
        'with-label': inputLabel,
      })}
    >
      {isLoading &&
        Array.from({ length: skeletonCount }, (_, index) => (
          <li
            key={`skeleton-loading-${filterKey}-${index}`}
            className="item d-flex align-items-center position-relative justify-content-between"
          >
            <div className="d-flex flex-column w-100">
              <SkeletonLoading width="100%" height={40} borderRadius={4} />
            </div>
          </li>
        ))}
      {!isLoading &&
        suggestions?.map((suggestion, index) => {
          const suggestionValue = getSuggestionValue(suggestion);
          const suggestionKey = getSuggestionKey(suggestion, index);
          const isSelected = index === selectedSuggestionIndex;
          const isCompany = isCompanySuggestion(suggestion);

          // For company_domain / website_domain use companyDomain; for company_ids use companyId; for competitors_company_name use companyName
          let groupNameForAction = suggestionValue;
          if (isCompany) {
            if (filterKey === 'competitors_company_name') {
              groupNameForAction = suggestion.companyName;
            } else if (
              (filterKey === 'company_domain' ||
                filterKey === 'website_domain') &&
              suggestion.companyDomain
            ) {
              groupNameForAction = suggestion.companyDomain;
            } else {
              groupNameForAction = String(suggestion.companyId);
            }
          }

          // Skip rendering company suggestions without domain when filter is company_domain or website_domain
          if (
            (filterKey === 'company_domain' ||
              filterKey === 'website_domain') &&
            isCompany &&
            !suggestion.companyDomain
          ) {
            return null;
          }

          return (
            <li
              key={suggestionKey}
              {...accessibleOnClick(() => onSelectOption(groupNameForAction))}
              className={classNames(
                'item d-flex align-items-center position-relative justify-content-between',
                { 'autocomplete-menu-list': isSelected },
              )}
            >
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center">
                  {isCompany && suggestion.companyLogoUrl && (
                    <img
                      src={suggestion.companyLogoUrl}
                      alt={suggestion.companyName}
                      className="autocomplete-company-logo mr-2"
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 2,
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  {isV2Response &&
                    isV2Suggestion(suggestion) &&
                    suggestion.emoji && (
                      <span className="autocomplete-emoji mr-2">
                        {suggestion.emoji}
                      </span>
                    )}
                  <TruncatedTextWithAutoTooltip
                    className="autocomplete-text"
                    text={suggestionValue}
                  />
                </div>
                {isCompany && suggestion.companyDomain && (
                  <TruncatedTextWithAutoTooltip
                    className="autocomplete-type mb-0 gray-txt-11"
                    text={suggestion.companyDomain}
                  />
                )}
                {isV2Response &&
                  isV2Suggestion(suggestion) &&
                  suggestion.type && (
                    <p className="autocomplete-type mb-0 gray-txt-11">
                      {suggestion.type}
                    </p>
                  )}
              </div>
              {!hideIncludeExclude && (
                <div className="include-exclude-icons">
                  <OverlayTooltip
                    placement={Placement.Bottom}
                    id={`tooltip-${suggestionKey}-include-child-${suggestionKey}`}
                    className="bs-tooltip-inner"
                    text={includeTooltip}
                  >
                    <CirclePlus
                      className={`circle-plus ${
                        isIncludeDisabled
                          ? 'include-disabled'
                          : 'include-enabled'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isIncludeDisabled) {
                          handleOnChange({
                            action: 'inputOption',
                            groupName: groupNameForAction,
                          });
                          setShowAutoComplete(false);
                        }
                      }}
                    />
                  </OverlayTooltip>

                  <OverlayTooltip
                    placement={Placement.Bottom}
                    id={`tooltip-${suggestionKey}-exclude-child-${suggestionKey}`}
                    className="bs-tooltip-inner"
                    text="Exclude"
                  >
                    <CircleMinus
                      className="circle-minus"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOnChange({
                          action: 'excludeOption',
                          groupName: groupNameForAction,
                        });
                        setShowAutoComplete(false);
                      }}
                    />
                  </OverlayTooltip>
                </div>
              )}
            </li>
          );
        })}
    </ul>
  );
};

export const RenderCheckboxGroup: React.FC<RenderCheckboxGroupProps> = ({
  label,
  name,
  checked,
  childItems,
  expanded,
  handleOnChange,
  filterKey,
  infoCircleToolTip,
  hideIncludeExclude = false,
  isIncludeDisabled,
  includeTooltip,
  isPhoneNumberNudgeOpen,
  onToggleExpand,
  tooltipText,
  parentLocked = false,
  onLockClick,
}) => {
  const isPhoneNumber = name === 'Mobile' || name === 'Phone';
  const isChecked =
    checked === true || (isPhoneNumber && isPhoneNumberNudgeOpen);
  const isExpanded = expanded?.includes(name);

  return (
    <div className="v2-checkbox-container">
      <div className="v2-checkbox">
        {childItems?.length > 0 && onToggleExpand && (
          <span className="minus-plus-icon">
            {isExpanded ? (
              <Minus onClick={() => onToggleExpand(name)} />
            ) : (
              <Plus onClick={() => onToggleExpand(name)} />
            )}
          </span>
        )}
        <Checkbox
          checked={isChecked}
          value={name}
          onChange={(check) => {
            if (parentLocked) {
              onLockClick?.();
              return;
            }
            if (!check || !isIncludeDisabled) {
              handleOnChange({ action: 'changeParent', groupName: name });
            }
          }}
          label={
            <span className="label-container">
              {tooltipText ? (
                <OverlayTooltip placement={Placement.Bottom} text={tooltipText}>
                  <span className="d-flex">
                    <TruncatedTextWithAutoTooltip
                      className="checkbox-label"
                      text={label}
                    />
                  </span>
                </OverlayTooltip>
              ) : (
                <TruncatedTextWithAutoTooltip
                  className="checkbox-label"
                  text={label}
                />
              )}
              <span className="checked-number">
                {childItems?.length > 0 && ` (${childItems?.length})`}
              </span>
              {infoCircleToolTip && (
                <OverlayTooltip
                  placement={Placement.Bottom}
                  text={infoCircleToolTip}
                >
                  <span className="ml-1 mt-1 gray-txt-11">
                    <InfoCircle width={13.33} height={13.33} />
                  </span>
                </OverlayTooltip>
              )}
              {parentLocked && hideIncludeExclude && (
                <span
                  {...(onLockClick
                    ? accessibleOnClick((e: any) => {
                        e?.stopPropagation();
                        onLockClick();
                      })
                    : {})}
                >
                  <Lock
                    width={14}
                    height={14}
                    color="#F59E0B"
                    className="accordion-lock-icon"
                  />
                </span>
              )}
            </span>
          }
          intermediate={checked === 'indeterminate'}
        />
        {!hideIncludeExclude && (
          <div className="include-exclude-icons">
            {parentLocked ? (
              <span
                {...(onLockClick
                  ? accessibleOnClick((e: any) => {
                      e?.stopPropagation();
                      onLockClick();
                    })
                  : {})}
              >
                <Lock
                  width={16}
                  height={16}
                  color="#F59E0B"
                  className="accordion-lock-icon"
                />
              </span>
            ) : (
              <>
                <OverlayTooltip
                  placement={Placement.Bottom}
                  id={`tooltip-${name}-include`}
                  className="bs-tooltip-inner"
                  text={includeTooltip}
                >
                  <CirclePlus
                    className={`circle-plus ${
                      isIncludeDisabled ? 'include-disabled' : 'include-enabled'
                    }`}
                    onClick={() => {
                      if (!isIncludeDisabled) {
                        handleOnChange({
                          action: 'includeParent',
                          groupName: name,
                        });
                      }
                    }}
                  />
                </OverlayTooltip>

                <OverlayTooltip
                  placement={Placement.Bottom}
                  id={`tooltip-${name}-exclude`}
                  className="bs-tooltip-inner"
                  text="Exclude"
                >
                  <CircleMinus
                    className="circle-minus"
                    onClick={() =>
                      handleOnChange({
                        action: 'excludeParent',
                        groupName: name,
                      })
                    }
                  />
                </OverlayTooltip>
              </>
            )}
          </div>
        )}
      </div>
      {isExpanded && (
        <div className="v2-checkbox-children">
          {childItems?.map((childItem) => (
            <div
              className="v2-checkbox"
              key={`${filterKey}-${name}-child-${childItem?.key}`}
            >
              <div
                className={classNames(' v2-checkbox-child', {
                  'locked-child-item': childItem?.isLocked,
                })}
                {...(childItem?.isLocked && onLockClick
                  ? accessibleOnClick((e: any) => {
                      e?.stopPropagation();
                      onLockClick();
                    })
                  : {})}
              >
                <Checkbox
                  key={childItem?.key}
                  value={childItem?.key}
                  disabled={!!childItem?.isLocked}
                  onChange={(check) => {
                    if (!check || !isIncludeDisabled) {
                      // prevent changing locked items
                      if (childItem?.isLocked) return;
                      handleOnChange({
                        action: 'changeChild',
                        groupName: name,
                        childName: childItem?.key,
                      });
                    }
                  }}
                  label={
                    <span className="label-container">
                      <div className="d-flex align-items-center w-100">
                        <TruncatedTextWithAutoTooltip
                          className="checkbox-label"
                          text={childItem?.value}
                        />
                        {childItem?.isLocked && (
                          <Lock
                            width={14}
                            height={14}
                            color="#F59E0B"
                            className="ml-2 accordion-lock-icon"
                          />
                        )}
                      </div>
                    </span>
                  }
                  checked={childItem?.checked}
                />
                {!hideIncludeExclude && (
                  <div className="include-exclude-icons">
                    {!childItem?.isLocked && (
                      <>
                        <OverlayTooltip
                          placement={Placement.Bottom}
                          id={`tooltip-${name}-include-child-${childItem?.key}`}
                          className="bs-tooltip-inner"
                          text={includeTooltip}
                        >
                          <CirclePlus
                            className={`circle-plus ${
                              isIncludeDisabled
                                ? 'include-disabled'
                                : 'include-enabled'
                            }`}
                            onClick={() => {
                              if (!isIncludeDisabled) {
                                handleOnChange({
                                  action: 'includeChild',
                                  groupName: name,
                                  childName: childItem?.key,
                                });
                              }
                            }}
                          />
                        </OverlayTooltip>

                        <OverlayTooltip
                          placement={Placement.Bottom}
                          id={`tooltip-${name}-exclude-child-${childItem?.key}`}
                          className="bs-tooltip-inner"
                          text="Exclude"
                        >
                          <CircleMinus
                            className="circle-minus"
                            onClick={() => {
                              handleOnChange({
                                action: 'excludeChild',
                                groupName: name,
                                childName: childItem?.key,
                              });
                            }}
                          />
                        </OverlayTooltip>
                      </>
                    )}
                    {childItem?.isLocked && (
                      <Lock
                        width={16}
                        height={16}
                        color="#F59E0B"
                        className="accordion-lock-icon"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
